// THE CHANGE HALF OF A LIVE READ, for a read that is not one `createQuery`.
//
// `createLiveQuery` (`live-query.ts`) re-reads by invalidating its query. A
// PAGED list cannot: its query is keyed on the cursor, so an invalidation
// re-reads only the last page loaded and the merge ignores a refetch at the same
// cursor (`docs/data-and-live-views.md` § Lists). Such a caller passes its own
// re-read — `paged.reload()` — and gets the same registration, matching,
// coalescing and liveness verdict `createLiveQuery` is built on. ONE
// implementation: `createLiveQuery` is this plus an invalidation.
//
// WHAT "VOUCHES" MEANS, exactly. The topic is served on a live connection
// (`statusForTopics`), AND every entity it holds by id is in the stream's store
// — or its entity has a signal-only topic covering every row (`task_changes`,
// `entities.ts`) and the connection serves it. The second clause is not
// decoration: each topic snapshots a bounded head (the newest
// `STREAM_SNAPSHOT_LIMIT` rows, `apps/api/src/routes/streams.ts`), so an older
// entity is never patched; a by-id read of it that trusted the topic alone would
// sit on its first answer with its poll off, for ever. A row that leaves the head
// arrives as a `deleted` patch — a change — so the read re-reads and, with the
// id no longer held, polls again.
//
// COALESCED, per read. A snapshot is a burst of patches; a busy fleet is a
// steady one. The first matching signal arms ONE `onChange`
// {@link LIVE_COALESCE_MS} later and every signal until it fires is absorbed, so
// a burst costs one re-read, never one per row.
//
// A CONNECTION'S `ready` RE-READS ONLY WHAT THE GAP COULD HAVE STALED, by the
// two facts on the signal (`change-feed.ts`): a read made before THIS
// connection's dial started (`since`) re-reads once, and so does every read of a
// topic the tab held rows for from the previous connection (`resumed`) — a row
// that vanished meanwhile arrives as no patch at all. A tab's first connection
// resumes nothing, so a fresh mount whose read started with the dial does not
// fetch twice. A topic the previous connection served live until OUR abort (a
// route change re-dialled for a new topic set) is `continued`: it had no gap, so
// neither rule re-reads it, or every route change re-read every live read.

import { createEffect, createMemo, onCleanup, untrack } from 'solid-js';
import type { AccountStreamStatus } from '../accountStream';
import { statusForTopics, useSharedHub } from '../stream-topics';
import {
  everyRowTopicOf,
  interestedIn,
  type LiveInterest,
  rowTopicOf,
  rowTopicsFor,
  topicsFor,
} from './entities';

/** How long a matched signal waits for its burst to finish before the one re-read. */
export const LIVE_COALESCE_MS = 100;

export type LiveChanges = {
  /** These interests' topics' connection status — the pill. `idle` with no shared stream. */
  readonly status: () => AccountStreamStatus;
  /** True while the stream vouches for every entity held: the caller may stop polling. */
  readonly isLive: () => boolean;
};

/**
 * Call `onChange` (coalesced) whenever the account stream says something
 * `interests` holds moved, or a connection serving its topics came up after a
 * gap the caller's data predates. `readAt` is when the caller's current data's
 * read STARTED (epoch ms; 0 for none yet) — required, because only the caller
 * knows. `token` must be the one the shell hub dialled with; any other caller
 * gets `idle`, never `live`, and no connection of its own (`useSharedHub`).
 */
export function useLiveChanges(
  interests: () => readonly LiveInterest[],
  token: string | null,
  onChange: () => void,
  readAt: () => number,
): LiveChanges {
  const hub = useSharedHub(token);
  // The joined string is the identity, so a re-evaluated interest list with
  // the same topics never re-registers (the hub's own memo rule).
  const topicKey = createMemo(() => topicsFor(interests()).join(','));
  const topics = (): string[] => (topicKey() === '' ? [] : topicKey().split(','));
  // Liveness is graded on the ROW topics only: an entity's signal-only
  // `everyRow` topic is optional (`entities.ts`), so a server without it must
  // not turn every read of that entity into a red, polling one.
  const rowKey = createMemo(() => rowTopicsFor(interests()).join(','));

  const status = (): AccountStreamStatus =>
    hub === undefined || rowKey() === ''
      ? 'idle'
      : statusForTopics(hub.stream, rowKey().split(','));
  /** A by-id interest is vouched for while its id is in the store, or its entity's `everyRow` topic is served. */
  const vouchedFor = (i: LiveInterest): boolean => {
    if (i.id === undefined || hub === undefined) return true;
    if (hub.stream.store[rowTopicOf(i.entity)]?.[i.id] !== undefined) return true;
    const everyRow = everyRowTopicOf(i.entity);
    return everyRow !== undefined && (hub.stream.served()?.has(everyRow) ?? false);
  };
  const isLive = (): boolean =>
    hub !== undefined && status() === 'live' && interests().every(vouchedFor);

  if (hub !== undefined) {
    createEffect(() => {
      const held = topics();
      if (held.length > 0) onCleanup(hub.subscribe(held));
    });
    let pending: ReturnType<typeof setTimeout> | undefined;
    const fire = (): void => {
      pending = undefined;
      onChange();
    };
    const stop = hub.stream.changes.listen((signals) => {
      if (pending !== undefined) return;
      const held = untrack(interests);
      const mine = untrack(topics);
      const moved = signals.some((s) => {
        if (s.kind !== 'ready') return interestedIn(held, s.topic, s.key);
        // Only the topics this connection did NOT carry over gap-free can be stale.
        const gapped = mine.filter((t) => s.subscribed.includes(t) && !s.continued.includes(t));
        return (
          gapped.length > 0 &&
          (gapped.some((t) => s.resumed.includes(t)) || untrack(readAt) < s.since)
        );
      });
      if (moved) pending = setTimeout(fire, LIVE_COALESCE_MS);
    });
    onCleanup(() => {
      stop();
      if (pending !== undefined) clearTimeout(pending);
    });
  }
  return { status, isLive };
}
